import React, { useMemo, useState } from "react";
import moment from "moment";
import "./interviewReadiness.scss";

// Shared by the voice-call entry pages (ScheduleStage and DynamicVoiceInterviewMain).
// Each page passes its own brand accent through the --ir-accent custom property.

export const BeforeYouBeginModal = ({ showNotReady, onStart, onNotReady, accentColor }) => (
  <div
    className="ir-overlay"
    role="dialog"
    aria-modal="true"
    style={accentColor ? { "--ir-accent": accentColor } : undefined}
  >
    <div className="ir-card">
      <h3>Before You Begin Your Interview</h3>
      <p>
        This interview takes <strong>10 to 20 minutes</strong> and can't be paused once started.
        Please begin only when you're in a quiet place with a stable internet connection.
      </p>
      <div className="ir-actions">
        {showNotReady && (
          <button type="button" className="ir-btn-secondary" onClick={onNotReady}>
            Not ready right now?
          </button>
        )}
        <button type="button" className="ir-btn-primary" onClick={onStart}>
          Start now
        </button>
      </div>
    </div>
  </div>
);

// ─── Resend response → UI outcome ─────────────────────────────────────────────
// Outcome types:
//   sent     – email went out; button stays only if remaining_resends > 0
//   noResend – resending can't work (no email / already resent); hide the button
//   blocked  – the link itself is unusable; show only the message and Close
//   retry    – transient or unexpected failure; keep the button enabled

const INVALID_LINK = "This link isn't valid. Please use the link from your email.";
const SEND_FAILED = "We couldn't send the email right now. Please try again in a moment.";
const GENERIC_ERROR = "Something went wrong. Please refresh the page and try again.";

const ALREADY_RESENT =
  "We've already resent the link to your email. Please check your inbox and spam folder. Still not received? Contact the recruiter.";

const NO_RESEND_DETAILS = {
  "No email address on file for this candidate":
    "We don't have an email address for you. Please use the link from your chat or contact the recruiter.",
  "The invite email has already been resent. Please check your inbox and spam folder.": ALREADY_RESENT,
};

const BLOCKED_DETAILS = {
  "This session is not a dynamic voice call": INVALID_LINK,
  "This stage is not a voice screening stage": INVALID_LINK,
  "This stage uses a dynamic voice call link": "Please use the voice screening link from your email.",
  "Invalid verification code": INVALID_LINK,
  "The Link has been expired": "This interview link has expired. Please contact the recruiter.",
  "This screening call has already been completed": "You've already completed this interview. Thank you!",
  "This screening call has already ended": "This interview session has ended. Check your email for a new link.",
  "This screening was closed due to inactivity":
    "This interview was closed due to inactivity. Please contact the recruiter.",
  "This screening is no longer available":
    "This interview is no longer available. Please contact the recruiter.",
  "Voice call session not found": INVALID_LINK,
  "Hiring stage not found": INVALID_LINK,
  "Your screening is already in progress": "Your interview is already in progress.",
  "Your call is already scheduled": "Your call is already scheduled. We'll call you at the time you chose.",
};

export const getResendOutcome = (res) => {
  if (res?.success) {
    return {
      type: "sent",
      maskedEmail: res.data?.masked_email || "",
      // Read the limit from the API rather than hard-coding it; missing → treat as used up.
      canResendAgain: Number(res.data?.remaining_resends) > 0,
    };
  }

  const status = res?.status || 0;
  const detail = (res?.detail || "").trim();

  if (NO_RESEND_DETAILS[detail]) return { type: "noResend", message: NO_RESEND_DETAILS[detail] };
  if (BLOCKED_DETAILS[detail]) return { type: "blocked", message: BLOCKED_DETAILS[detail] };

  // 429 = the candidate's resend was already used (e.g. reloaded or another device).
  if (status === 429) return { type: "noResend", message: ALREADY_RESENT };
  // Unrecognised 403/404/409 still mean the link can't be used from here.
  if ([403, 404, 409].includes(status)) return { type: "blocked", message: detail || INVALID_LINK };
  if (status >= 500) return { type: "retry", message: SEND_FAILED };
  return { type: "retry", message: GENERIC_ERROR };
};

// onResend resolves to the resendInterviewInvite result.
// onCloseClick is an optional side-effect hook (e.g. analytics) run before closing.
export const NotReadyNotice = ({ onResend, onCloseClick, accentColor }) => {
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [closeFailed, setCloseFailed] = useState(false);
  const deadline = useMemo(() => moment().add(1, "day").format("YYYY-MM-DD"), []);

  const type = outcome?.type;
  const showResendButton =
    type !== "noResend" && type !== "blocked" && !(type === "sent" && !outcome.canResendAgain);

  const handleResend = async () => {
    if (sending || !onResend) return;
    setSending(true);
    try {
      setOutcome(getResendOutcome(await onResend()));
    } catch (err) {
      console.error("Failed to resend interview email", err);
      setOutcome({ type: "retry", message: SEND_FAILED });
    } finally {
      setSending(false);
    }
  };

  // Browsers only let a script close a tab it opened itself, so fall back to
  // telling the candidate to close it manually if the tab is still open.
  const handleClose = () => {
    onCloseClick?.();
    window.close();
    setTimeout(() => setCloseFailed(true), 300);
  };

  let statusMessage = null;
  if (type === "sent") {
    statusMessage = (
      <p className="ir-resent-msg">
        We have sent you mail once again
        {outcome.maskedEmail ? ` at ${outcome.maskedEmail}` : ""}. It may take a few minutes to
        arrive, so check your spam folder too.
      </p>
    );
  } else if (outcome?.message) {
    statusMessage = <p className="ir-resend-error">{outcome.message}</p>;
  }

  return (
    <div
      className="ir-not-ready"
      style={accentColor ? { "--ir-accent": accentColor } : undefined}
    >
      {type !== "blocked" && (
        <>
          <h4>Not ready right now?</h4>
          <p>
            Complete your interview anytime before <strong>{deadline}</strong>. We've emailed you
            the link, so check your inbox. If you haven’t received the email, click Resend Email to
            receive the link again.
          </p>
        </>
      )}
      {statusMessage}
      {closeFailed ? (
        <p className="ir-close-fallback">You can now safely close this browser tab.</p>
      ) : (
        <div className="ir-actions">
          {showResendButton && (
            <button
              type="button"
              className="ir-btn-secondary"
              onClick={handleResend}
              disabled={sending}
            >
              {sending ? "Sending…" : "Resend Email"}
            </button>
          )}
          <button type="button" className="ir-btn-primary" onClick={handleClose}>
            Close
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Queue waiting / live-call screen ─────────────────────────────────────────
// Full-page screen shown while the candidate waits for an interviewer slot and,
// on ScheduleStage, during the web call itself. Kept on a light neutral
// background with calm copy: a full-bleed blue fullscreen page with an all-caps
// "do NOT refresh" banner matches browser tech-support-scam heuristics.

const formatQueueTimer = (totalSeconds) =>
  `${String(Math.floor(totalSeconds / 60)).padStart(2, "0")}:${String(totalSeconds % 60).padStart(2, "0")}`;

const QUEUE_TIPS = [
  {
    text: "Find a quiet place without background noise",
    icon: "M12 3v10.55A4 4 0 1014 17V7h4V3h-6z",
  },
  {
    text: "Speak clearly and loud enough to be heard",
    icon: "M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z",
  },
  {
    text: "Stay on a stable internet connection",
    icon: "M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.24 4.24 0 00-6 0zm-4-4l2 2a7.07 7.07 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z",
  },
];

export const QueueWaitingScreen = ({
  logoSrc,
  accentColor,
  waitSeconds = null,
  live = false,
  liveIndicator = null,
  title,
  subtitle,
  footer = null,
}) => {
  const hasTimer = !live && waitSeconds !== null && waitSeconds !== undefined;

  const defaultTitle = live
    ? "Interview in progress"
    : hasTimer
      ? "You're in the queue"
      : "Connecting you to the interviewer";
  const defaultSubtitle = live
    ? "Answer naturally and wait for the interviewer to finish speaking before you reply."
    : hasTimer
      ? "We'll connect you automatically as soon as an interviewer is available."
      : "This usually takes just a few moments.";

  return (
    <div
      className="ir-queue"
      style={accentColor ? { "--ir-accent": accentColor } : undefined}
    >
      <div className="ir-queue-card">
        {logoSrc && <img src={logoSrc} alt="" className="ir-queue-logo" />}

        <div className={`ir-queue-orb${live ? " live" : ""}`}>
          {live ? (
            liveIndicator
          ) : hasTimer ? (
            <div className="ir-queue-timer">
              <span className="ir-queue-timer-label">Estimated wait</span>
              <span className="ir-queue-timer-value">{formatQueueTimer(waitSeconds)}</span>
            </div>
          ) : (
            <span className="ir-queue-spinner" role="status">
              <span className="visually-hidden">Connecting…</span>
            </span>
          )}
        </div>

        <h2>{title || defaultTitle}</h2>
        <p className="ir-queue-subtitle">{subtitle || defaultSubtitle}</p>

        <ul className="ir-queue-tips">
          {QUEUE_TIPS.map((tip) => (
            <li key={tip.text}>
              <span className="ir-queue-tip-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d={tip.icon} />
                </svg>
              </span>
              {tip.text}
            </li>
          ))}
        </ul>

        <div className="ir-queue-notice">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          <span>Please keep this tab open. Refreshing or going back may end your session.</span>
        </div>

        {footer}
      </div>
    </div>
  );
};
